<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { api } from '../api'
import { acceptSession } from '../state'
import { isEnglish } from '../i18n'

const router = useRouter()
const tab = ref('login')
const form = ref({ username: '', display_name: '', password: '', invite_code: '' })
const error = ref('')
const busy = ref(false)
const l = computed(() => isEnglish.value ? {titleLogin:'Keep growing your word garden',titleRegister:'Join Wortgarten with an invite',heading:'One word, one sentence, one garden that slowly blooms.',copy:'Learn by Berliner Platz chapters and make A1/A2 vocabulary stick through recognition, spelling, and dictation.',alt:'A blooming chamomile word garden',garden:'German vocabulary garden',login:'Log in',register:'Register with invite',username:'Username',display:'Display name',password:'Password',invite:'Invite code',usernameHint:'e.g. mia',displayHint:'What should we call you?',passwordHint:'At least 10 characters',inviteHint:'Enter the code from your administrator',wait:'Please wait…',enter:'Enter garden',create:'Create account',note:'Your learning data is private and syncs between phone and computer.'} : {titleLogin:'继续浇灌你的词汇花园',titleRegister:'用邀请码加入 Wortgarten',heading:'一个词，一个句子，一座慢慢开花的花园。',copy:'按《柏林广场》章节学习，用选择、拼写和听写把 A1/A2 词汇真正记住。',alt:'盛开的洋甘菊词汇花园',garden:'德语词汇花园',login:'登录',register:'邀请码注册',username:'用户名',display:'显示名称',password:'密码',invite:'邀请码',usernameHint:'例如：mia',displayHint:'怎么称呼你',passwordHint:'至少 10 位',inviteHint:'输入管理员给你的邀请码',wait:'请稍候…',enter:'进入花园',create:'创建账号',note:'学习记录保存在私人账号中，可在手机和电脑间同步。'})
const title = computed(() => tab.value === 'login' ? l.value.titleLogin : l.value.titleRegister)

async function submit() {
  busy.value = true; error.value = ''
  try {
    const path = tab.value === 'login' ? '/api/auth/login' : '/api/auth/register'
    const data = await api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form.value) })
    acceptSession(data); router.push('/')
  } catch (e) { error.value = e.message } finally { busy.value = false }
}
</script>

<template>
  <div class="auth-page">
    <!-- 环境光斑：登录页在 App 壳外渲染，需自带奶玻璃背景（fixed 定位，不占网格列） -->
    <div class="ambient-bg" aria-hidden="true"><i class="blob blob-1"></i><i class="blob blob-2"></i><i class="blob blob-3"></i></div>
    <section class="auth-illustration">
      <div class="auth-copy"><p class="eyebrow">DEUTSCH · JEDEN TAG</p><h1>{{l.heading}}</h1><p>{{l.copy}}</p></div>
      <img src="/garden-stage-5.png" :alt="l.alt" />
    </section>
    <section class="auth-card">
      <div class="brand auth-brand"><span class="brand-mark">W</span><span><b>Wortgarten</b><small>{{l.garden}}</small></span></div>
      <div class="auth-tabs"><button :class="{ active: tab === 'login' }" @click="tab='login'">{{l.login}}</button><button :class="{ active: tab === 'register' }" @click="tab='register'">{{l.register}}</button></div>
      <h2>{{ title }}</h2>
      <form @submit.prevent="submit">
        <label><span>{{l.username}}</span><input v-model.trim="form.username" autocomplete="username" minlength="3" required :placeholder="l.usernameHint" /></label>
        <label v-if="tab === 'register'"><span>{{l.display}}</span><input v-model.trim="form.display_name" required :placeholder="l.displayHint" /></label>
        <label><span>{{l.password}}</span><input v-model="form.password" type="password" :autocomplete="tab === 'login' ? 'current-password' : 'new-password'" minlength="10" required :placeholder="l.passwordHint" /></label>
        <label v-if="tab === 'register'"><span>{{l.invite}}</span><input v-model.trim="form.invite_code" required :placeholder="l.inviteHint" /></label>
        <p v-if="error" class="form-error">{{ error }}</p>
        <button class="primary-button" :disabled="busy">{{ busy ? l.wait : (tab === 'login' ? l.enter : l.create) }}</button>
      </form>
      <p class="auth-note">{{l.note}}</p>
    </section>
  </div>
</template>

<style scoped>
/* ============================================================
   Stage 3 · 视图改造（门户组）：登录/注册页奶玻璃化
   左侧插画面板为自含亮底卡片，维持原样；
   仅令牌化右侧表单栏（替换写死 #fff 的 input 与 tab active）。
   ============================================================ */

/* 右侧表单栏：重玻璃栏级面板（暗色令牌自动翻转） */
.auth-card {
  background: var(--glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%);
}

/* 登录/注册切换：轨道 inset，激活态浮起玻璃（替换写死 #fff） */
.auth-tabs { border: 1px solid var(--glass-border); background: var(--glass-bg-inset); }
.auth-tabs button { min-height: 44px; }
.auth-tabs button.active { background: var(--glass-raised-bg); box-shadow: var(--glass-raised-shadow); color: var(--ink); }

/* 输入框：软玻璃 input 规范（替换写死 #fff） */
.auth-card input {
  border-color: var(--glass-border);
  background: var(--glass-bg-soft);
  -webkit-backdrop-filter: blur(var(--glass-blur-soft)) saturate(150%);
  backdrop-filter: blur(var(--glass-blur-soft)) saturate(150%);
  color: var(--ink);
}
.auth-card input::placeholder { color: var(--muted-2); }
.auth-card input:focus { border-color: var(--sage); outline: none; box-shadow: 0 0 0 3px var(--sage-glow); }

/* 降级：不支持 backdrop-filter 时回退近实色 */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .auth-card, .auth-card input { background: var(--glass-fallback); -webkit-backdrop-filter: none; backdrop-filter: none; }
}
</style>
